import { coerceEnumByLabel } from '../../lib/api/coerceEnum';
import type { TaskItemStatus, TaskPriority, TaskType } from './types';

export const TASK_STATUS_LABELS: Record<TaskItemStatus, string> = {
  0: 'Open',
  1: 'In Progress',
  2: 'Blocked',
  3: 'Completed',
  4: 'Cancelled',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical',
};

export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  0: 'default',
  1: 'blue',
  2: 'orange',
  3: 'red',
};

export const TASK_STATUS_COLORS: Record<TaskItemStatus, string> = {
  0: 'blue',
  1: 'processing',
  2: 'warning',
  3: 'success',
  4: 'default',
};

export const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS_LABELS).map(
  ([value, label]) => ({ value: Number(value) as TaskItemStatus, label })
);

export const TASK_PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(
  ([value, label]) => ({ value: Number(value) as TaskPriority, label })
);

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  0: 'Internal task',
  1: 'Customer follow-up',
  2: 'Recurring task',
};

export const TASK_TYPE_SHORT_LABELS: Record<TaskType, string> = {
  0: 'Internal',
  1: 'Follow-up',
  2: 'Recurring',
};

export const TASK_TYPE_TAG_COLORS: Record<TaskType, string> = {
  0: 'default',
  1: 'blue',
  2: 'purple',
};

export const TASK_TYPE_OPTIONS = Object.entries(TASK_TYPE_LABELS).map(
  ([value, label]) => ({ value: Number(value) as TaskType, label })
);

export const TASK_TYPE_FILTER_OPTIONS: Array<{ value: TaskType; label: string }> = [
  { value: 0 as TaskType, label: TASK_TYPE_LABELS[0] },
  { value: 1 as TaskType, label: TASK_TYPE_LABELS[1] },
  { value: 2 as TaskType, label: TASK_TYPE_LABELS[2] },
];

export const TASK_TYPE_FOLLOWUP: TaskType = 1;
export const TASK_TYPE_INTERNAL: TaskType = 0;
export const TASK_TYPE_RECURRING: TaskType = 2;

/** Backend `TaskType` JSON names (`JsonStringEnumConverter`). */
export const TASK_TYPE_BY_API_NAME: Record<string, TaskType> = {
  Internal: TASK_TYPE_INTERNAL,
  Followup: TASK_TYPE_FOLLOWUP,
  Recurring: TASK_TYPE_RECURRING,
};

export const RECURRING_DUE_DATE_PRESETS = [
  { days: 15, label: 'In 15 days' },
  { days: 30, label: 'In 30 days' },
  { days: 60, label: 'In 60 days' },
] as const;

export function isUnlinkedTaskType(taskType: TaskType): boolean {
  return taskType === TASK_TYPE_INTERNAL || taskType === TASK_TYPE_RECURRING;
}

const TASK_STATUS_BY_API_NAME: Record<string, TaskItemStatus> = {
  Open: 0,
  InProgress: 1,
  Blocked: 2,
  Completed: 3,
  Cancelled: 4,
};

/** Read task type from API payloads (camelCase, PascalCase, or legacy `type`). */
export function readTaskTypeField(item: {
  taskType?: unknown;
  TaskType?: unknown;
  type?: unknown;
  Type?: unknown;
}): unknown {
  return item.taskType ?? item.TaskType ?? item.type ?? item.Type;
}

export function normalizeTaskType(value: unknown): TaskType {
  const raw =
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? readTaskTypeField(value as { taskType?: unknown; TaskType?: unknown; type?: unknown; Type?: unknown })
      : value;

  const normalized = coerceEnumByLabel(raw, TASK_TYPE_BY_API_NAME);
  if (normalized !== undefined) {
    return normalized;
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    const row = value as { taskType?: unknown; contactId?: string | null };
    if (row.taskType === 'Followup' || row.taskType === 1) {
      return TASK_TYPE_FOLLOWUP;
    }
    if (row.contactId) {
      return TASK_TYPE_FOLLOWUP;
    }
  }

  return TASK_TYPE_INTERNAL;
}

export function readTaskStatusField(item: {
  status?: unknown;
  Status?: unknown;
}): unknown {
  return item.status ?? item.Status;
}

export function normalizeTaskStatus(value: unknown): TaskItemStatus {
  const raw =
    value !== null && typeof value === 'object' && !Array.isArray(value)
      ? readTaskStatusField(value as { status?: unknown; Status?: unknown })
      : value;

  return coerceEnumByLabel(raw, TASK_STATUS_BY_API_NAME) ?? 0;
}

export const TASK_STATUS_TERMINAL: TaskItemStatus[] = [3, 4];

export function isTerminalStatus(status: TaskItemStatus | unknown): boolean {
  const normalized = typeof status === 'number' ? status : normalizeTaskStatus(status);
  return normalized === 3 || normalized === 4;
}

export function isOpenFollowup(taskType: TaskType, status: TaskItemStatus): boolean {
  return taskType === TASK_TYPE_FOLLOWUP && !isTerminalStatus(status);
}

export function isOpenRecurring(taskType: TaskType, status: TaskItemStatus): boolean {
  return taskType === TASK_TYPE_RECURRING && !isTerminalStatus(status);
}

export function getSelectableStatuses(
  taskType: TaskType,
  currentStatus: TaskItemStatus,
): { value: TaskItemStatus; label: string }[] {
  if (isTerminalStatus(currentStatus)) {
    return [];
  }

  if (taskType === TASK_TYPE_FOLLOWUP) {
    return TASK_STATUS_OPTIONS.filter((o) => o.value === 0 || o.value === 1 || o.value === 2);
  }

  if (taskType === TASK_TYPE_RECURRING) {
    return TASK_STATUS_OPTIONS.filter((o) => o.value !== 3);
  }

  return TASK_STATUS_OPTIONS;
}
